import { useState } from 'react';
import '../../styles/editcase/ProcessingWarnings.css';

// Short labels for the fixed steps. Extraction groups and documents carry
// their own name after the colon and are labelled from it below.
const STEP_LABELS = {
    red_flags: 'Red flags',
    diagnosis_codes: 'Diagnosis codes',
    timeline: 'Timeline',
    hpi: 'HPI',
    diagnosis: 'Diagnosis',
    file_path: 'File path',
    identity: 'Identity check',
};

export function stepLabel(step) {
    if (typeof step !== 'string' || !step) return '';
    if (step.startsWith('extraction:')) return `Extraction: ${step.slice('extraction:'.length)}`;
    if (step.startsWith('document:')) return `Document: ${step.slice('document:'.length)}`;
    return STEP_LABELS[step] || step;
}

// Structured entries are {code, step, message, at, resolved}; entries written
// before F2 are plain strings and show as one row each with no step and no
// retry, since nothing on the server knows which step they belong to.
export function unresolvedWarnings(warnings) {
    if (!Array.isArray(warnings)) return [];
    return warnings.filter((entry) => {
        if (typeof entry === 'string') return entry.trim() !== '';
        return entry && typeof entry === 'object' && entry.resolved !== true;
    });
}

export default function ProcessingWarnings({ warnings, status, canRetry, onRetry }) {
    const [retrying, setRetrying] = useState(false);
    const rows = unresolvedWarnings(warnings);
    const working = status && typeof status === 'object' && status.condition === 'working';
    if (rows.length === 0 || working) return null;

    const retryable = rows.some((entry) => typeof entry !== 'string');
    const showRetry = retryable && Boolean(canRetry) && typeof onRetry === 'function';

    const handleRetry = async () => {
        if (retrying) return;
        setRetrying(true);
        try {
            await onRetry();
        } catch (error) {
            // The caller reports the failure; the button only needs to come back.
        } finally {
            setRetrying(false);
        }
    };

    return (
        <div className="processing-warnings" role="status">
            <div className="processing-warnings-header">
                <span className="processing-warnings-title">
                    {rows.length === 1
                        ? 'One processing step needs attention'
                        : `${rows.length} processing steps need attention`}
                </span>
                {showRetry && (
                    <button
                        type="button"
                        className="processing-warnings-retry"
                        onClick={handleRetry}
                        disabled={retrying}
                    >
                        {retrying ? 'Retrying...' : 'Retry failed steps'}
                    </button>
                )}
            </div>
            <ul className="processing-warnings-list">
                {rows.map((entry, index) => {
                    if (typeof entry === 'string') {
                        return (
                            <li key={`legacy-${index}`} className="processing-warnings-row">
                                <span className="processing-warnings-message">{entry}</span>
                            </li>
                        );
                    }
                    return (
                        <li key={`${entry.step || entry.code || 'warning'}-${index}`} className="processing-warnings-row">
                            {entry.step && (
                                <span className="processing-warnings-step">{stepLabel(entry.step)}</span>
                            )}
                            <span className="processing-warnings-message">{entry.message}</span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
